/**
 * The warnings engine.
 *
 * Everything Lunchly wants to say about a week that is *not* simply "here are
 * the lunches" is produced here, once, as data. Components never re-derive a
 * problem from the schedule: they render `Warning` objects. That is what keeps
 * the day card, the timeline, the warnings panel and the export preview all
 * describing the same fault in the same words.
 *
 * Three properties matter more than the individual checks:
 *
 *  1. **Nothing is ever refused.** A validator that blocked a schedule would be
 *     wrong every time a manager knew something the rota did not (Josh's 14:00
 *     lunch one hour into a 13:00 start is a real, correct decision). Warnings
 *     describe; the manager decides.
 *  2. **Ids are deterministic**, built only from `code`, `dayKey`, `employeeId`
 *     and `slotMinutes`. Regenerating a week produces the same id for the same
 *     complaint, so an acknowledgement ("yes, I meant that") survives instead of
 *     the manager having to dismiss the same warning after every edit.
 *  3. **`detail` carries the real numbers.** "Coverage is low" is unactionable;
 *     "cover drops to 1 against a minimum of 2 between 13:00 and 14:00" tells a
 *     manager exactly which lunch to move.
 *
 * Pure logic: no DOM, no storage, and nothing handed in is mutated or retained.
 */

import {
  DAY_KEYS,
  DAY_LABELS,
  formatDuration,
  formatMinutes,
  isWeekend,
} from '../core/time.js';
import {
  DEFAULT_SETTINGS,
  EVENING_SINGLE_LUNCH_FROM_MINUTES,
  NISCC_SINGLE_LUNCH_CUTOFF_MINUTES,
  resolveDayConfig,
} from '../core/settings.js';
import { ASSIGNMENT_SOURCES, buildWeekShifts, ruleFor } from '../core/model.js';
import { slugify } from '../core/ids.js';
import { assessDay } from './eligibility.js';
import { computeCoverage } from './coverage.js';

/**
 * Warning codes. Every one of these is produced by a branch below - a code the
 * engine can never emit is a promise to the UI that the UI cannot keep.
 */
export const WARNING_CODES = Object.freeze({
  /** Owed a lunch by the rules, but the week contains no assignment for them. */
  ELIGIBLE_NO_LUNCH: 'eligible-no-lunch',
  /** Has a lunch although the rules say they are not entitled to one. */
  INELIGIBLE_ASSIGNED: 'ineligible-assigned',
  /** The break does not sit fully inside the shift it belongs to. */
  OUTSIDE_SHIFT: 'outside-shift',
  /** Break starts before the settling-in guard rail allows. */
  TOO_SOON_AFTER_START: 'too-soon-after-start',
  /** Break finishes too near the end of the shift to be a real break. */
  TOO_CLOSE_TO_END: 'too-close-to-end',
  /** Too few people left answering phones for part of the day. */
  COVERAGE_BELOW_MINIMUM: 'coverage-below-minimum',
  /** More people at lunch at once than the day allows. */
  TOO_MANY_SIMULTANEOUS: 'too-many-simultaneous',
  /** Two rota rows resolve to one employee id. */
  DUPLICATE_EMPLOYEE: 'duplicate-employee',
  /** A rota cell that cannot be trusted: unreadable, zero-length or absurd. */
  INVALID_SHIFT: 'invalid-shift',
  /** A short name from a historical list that nobody has mapped yet. */
  UNRESOLVED_ALIAS: 'unresolved-alias',
  /** The rota came out of a PDF the importer was not confident about. */
  LOW_CONFIDENCE_IMPORT: 'low-confidence-import',
  /** The same person has two lunches on one day. */
  DOUBLE_ASSIGNED: 'double-assigned',
  /** The solver could not place everybody who was owed a lunch. */
  NO_SOLUTION: 'no-solution',
});

const SEVERITY = Object.freeze({ ERROR: 'error', WARNING: 'warning', INFO: 'info' });

/** Sort order for severities; also the key order of `counts`. */
const SEVERITY_RANK = Object.freeze({ error: 0, warning: 1, info: 2 });

/**
 * Breach codes as written by `coverage.js`. They are not exported there, so they
 * are mirrored here and every read goes through {@link hasBreach}, which falls
 * back to the bucket's own numbers if the list is ever missing (a schedule
 * restored from an older save, say).
 */
const BREACH_BELOW_MINIMUM = 'below-minimum';
const BREACH_TOO_MANY_SIMULTANEOUS = 'too-many-simultaneous';

/**
 * Longest shift we will treat as real. Beyond this the cell is almost certainly
 * a typo (`08:00-08:00` read as a whole day, a missing digit), and scheduling a
 * lunch around invented times is worse than saying so.
 */
const MAX_PLAUSIBLE_SHIFT_MINUTES = 16 * 60;

/** Import confidence below this is worth a second pair of eyes. */
const LOW_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Validate a whole generated week.
 *
 * Runs the same eligibility and coverage maths the solver used, then compares
 * that against what the schedule actually contains. Re-running the rules rather
 * than trusting notes left behind by the solver is deliberate: assignments can
 * be dragged, locked, imported or hand-typed long after generation, and those
 * are precisely the edits a manager most needs checked.
 *
 * The returned `warnings` array is the complete, sorted list. `byDay` indexes
 * only the day-scoped subset, so a caller wiring the result onto a Schedule
 * writes `days[key].warnings = byDay[key]` and
 * `schedule.warnings = warnings.filter((w) => w.dayKey === null)` - week-scoped
 * warnings appear exactly once, never duplicated onto all seven days.
 *
 * @param {{ rota?: object, weekShifts?: object, schedule?: object, settings?: object,
 *           rulesIndex?: Map, aliasBook?: object }} input
 *   `weekShifts` is the output of `buildWeekShifts`; it is rebuilt from `rota`
 *   when omitted so a caller holding only a rota still gets a full answer.
 * @returns {{ warnings: object[], byDay: Record<string, object[]>,
 *             counts: { error: number, warning: number, info: number } }}
 */
export function validateWeek({
  rota,
  weekShifts,
  schedule,
  settings,
  rulesIndex,
  aliasBook,
} = {}) {
  const resolvedSettings = settings ?? DEFAULT_SETTINGS;
  const shiftsByDay = resolveWeekShifts(weekShifts, rota, resolvedSettings);
  const names = buildNameIndex(rota, shiftsByDay, schedule);
  const acknowledgedIds = collectAcknowledgedIds(schedule);

  const collected = [];
  for (const dayKey of DAY_KEYS) {
    collected.push(...validateDay({
      dayKey,
      shifts: shiftsByDay[dayKey] ?? [],
      day: schedule?.days?.[dayKey] ?? null,
      settings: resolvedSettings,
      rulesIndex,
      names,
    }));
  }
  collected.push(...validateWeekScope({ rota, aliasBook, names }));

  // Acknowledgement is applied in one pass so that `warnings` and `byDay` can
  // never disagree about whether a warning has been overridden.
  const warnings = collected
    .map((item) => ({ ...item, acknowledged: acknowledgedIds.has(item.id) }))
    .sort(compareWarnings);

  const byDay = {};
  for (const dayKey of DAY_KEYS) byDay[dayKey] = [];
  const counts = { error: 0, warning: 0, info: 0 };

  for (const item of warnings) {
    if (item.dayKey && byDay[item.dayKey]) byDay[item.dayKey].push(item);
    if (counts[item.severity] !== undefined) counts[item.severity] += 1;
  }

  return { warnings, byDay, counts };
}

/* -------------------------------------------------------------------------- */
/* One day                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * All day-scoped checks for a single day.
 *
 * Ordered the way a manager reads a day card: what the rota says, then what each
 * person was or was not given, then how the day holds together. The later passes
 * also depend on the indexes the earlier ones build, so the order is not purely
 * presentational.
 */
function validateDay({ dayKey, shifts, day, settings, rulesIndex, names }) {
  const warnings = [];
  const dayConfig = resolveDayConfig(settings, dayKey);
  const assignments = Array.isArray(day?.assignments) ? day.assignments : [];
  const lunchMinutes = positive(settings?.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);

  const entries = assessDay(shifts, { settings, dayConfig, rulesIndex });
  const entryByEmployee = new Map();
  for (const entry of entries) {
    if (entry.employeeId) entryByEmployee.set(entry.employeeId, entry);
  }

  const assignmentsByEmployee = new Map();
  for (const assignment of assignments) {
    const employeeId = assignment?.employeeId;
    if (!employeeId) continue;
    if (!assignmentsByEmployee.has(employeeId)) assignmentsByEmployee.set(employeeId, []);
    assignmentsByEmployee.get(employeeId).push(assignment);
  }

  /* 1. Rota cells we could not make sense of. ---------------------------- */
  for (const shift of shifts) {
    const invalid = invalidShiftWarning(shift, dayKey, names);
    if (invalid) warnings.push(invalid);
  }

  /* 2. Person by person: owed vs given, and where the break landed. ------ */
  for (const entry of entries) {
    const placed = assignmentsByEmployee.get(entry.employeeId) ?? [];

    if (entry.eligible && placed.length === 0) {
      warnings.push(eligibleNoLunchWarning(entry, day, dayKey));
      continue;
    }
    if (!entry.eligible && placed.length > 0) {
      warnings.push(ineligibleAssignedWarning(entry, placed[0], dayKey, names));
    }
  }

  // Assignments for somebody who is not on this week's rota at all still need
  // an explanation - they are the commonest symptom of a name change or a
  // half-finished import.
  for (const [employeeId, placed] of assignmentsByEmployee) {
    if (entryByEmployee.has(employeeId)) continue;
    warnings.push(unknownEmployeeAssignedWarning(placed[0], dayKey, names));
  }

  /* 3. Placement checks against the shift the break belongs to. ---------- */
  for (const assignment of assignments) {
    const entry = entryByEmployee.get(assignment?.employeeId) ?? null;
    warnings.push(...placementWarnings({
      assignment,
      shift: entry?.shift ?? null,
      rule: ruleFor(rulesIndex, assignment?.employeeId),
      dayConfig,
      lunchMinutes,
      dayKey,
      names,
    }));
  }

  /* 4. Two lunches for one person on one day. ---------------------------- */
  for (const [employeeId, placed] of assignmentsByEmployee) {
    if (placed.length < 2) continue;
    warnings.push(doubleAssignedWarning(employeeId, placed, dayKey, names));
  }

  /* 5. The day as a whole. ----------------------------------------------- */
  const buckets = resolveCoverage({ day, shifts, assignments, dayConfig, settings });
  warnings.push(...coverageWarnings(buckets, dayKey, names));

  if (day && day.solved === false) {
    warnings.push(noSolutionWarning(day, dayKey, names));
  }

  return warnings;
}

/* -------------------------------------------------------------------------- */
/* Per-person checks                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A rota cell that cannot be trusted.
 *
 * Unreadable text, a shift with no length and an implausibly long shift are one
 * warning rather than three codes because the manager's next action is the same
 * for all of them: open the rota editor and fix the cell.
 */
function invalidShiftWarning(shift, dayKey, names) {
  if (!shift) return null;
  const name = nameOf(names, shift.employeeId, shift.name);
  const day = DAY_LABELS[dayKey] ?? dayKey;

  if (shift.kind === 'unparsed') {
    const raw = String(shift.raw ?? '').trim();
    return warning({
      code: WARNING_CODES.INVALID_SHIFT,
      severity: SEVERITY.WARNING,
      dayKey,
      employeeId: shift.employeeId,
      message: `${name}'s ${day} shift could not be read`,
      detail: `The ${day} cell for ${name} reads "${raw}", which is not a start and end time`
        + `${shift.parseReason ? ` (${shift.parseReason})` : ''}. ${name} is being left out of `
        + `${day}'s lunches until the cell is corrected.`,
    });
  }

  if (shift.kind !== 'shift') return null;

  const duration = Number(shift.durationMinutes);
  if (!Number.isFinite(duration) || duration <= 0) {
    return warning({
      code: WARNING_CODES.INVALID_SHIFT,
      severity: SEVERITY.WARNING,
      dayKey,
      employeeId: shift.employeeId,
      message: `${name}'s ${day} shift has no length`,
      detail: `The ${day} cell for ${name} reads "${String(shift.raw ?? '').trim()}", which starts `
        + `and finishes at ${formatMinutes(shift.startMinutes)}. A zero-length shift cannot hold a `
        + 'lunch - correct the times in the rota editor.',
    });
  }

  if (duration > MAX_PLAUSIBLE_SHIFT_MINUTES) {
    return warning({
      code: WARNING_CODES.INVALID_SHIFT,
      severity: SEVERITY.WARNING,
      dayKey,
      employeeId: shift.employeeId,
      message: `${name}'s ${day} shift is ${formatDuration(duration)} long`,
      detail: `${name} is down for ${shiftRange(shift)} on ${day}, which is `
        + `${formatDuration(duration)} - longer than the ${formatDuration(MAX_PLAUSIBLE_SHIFT_MINUTES)} `
        + 'a single shift can plausibly run, so the cell is probably a typo. The times have been '
        + 'used exactly as written.',
    });
  }

  return null;
}

/** Owed a lunch, but the week has none for them. */
function eligibleNoLunchWarning(entry, day, dayKey) {
  const name = entry.name || entry.employeeId;
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;
  const unassigned = (day?.unassigned ?? []).find((record) => record.employeeId === entry.employeeId);
  const shiftText = entry.shift ? `${shiftRange(entry.shift)} (${formatDuration(entry.shift.durationMinutes)})` : 'their shift';

  const because = unassigned?.reasonText
    ? ` The solver could not place one: ${unassigned.reasonText}`
    : ' Nothing in the schedule covers them, so the break has been lost rather than declined.';

  return warning({
    code: WARNING_CODES.ELIGIBLE_NO_LUNCH,
    severity: SEVERITY.WARNING,
    dayKey,
    employeeId: entry.employeeId,
    message: `${name} is owed a lunch on ${dayLabel} but has none`,
    detail: `${name} works ${shiftText} on ${dayLabel}. ${entry.reasonText}${because}`,
  });
}

/**
 * A lunch for somebody the rules say is not entitled to one.
 *
 * A hand-placed lunch is reported as `info`, not a warning: the manager already
 * knows, and the rules are the thing that is probably incomplete. Reporting it
 * at all still matters - it is how the day card can show *why* somebody appears
 * on a list they would not otherwise be on.
 */
function ineligibleAssignedWarning(entry, assignment, dayKey, names) {
  const name = nameOf(names, entry.employeeId, entry.name);
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;
  const slot = slotOf(assignment);
  const at = slot === null ? 'a lunch' : `a lunch at ${formatMinutes(slot)}`;
  const chosen = humanChoice(assignment);

  if (chosen) {
    // The manager's own words end the sentence, so any full stop they typed is
    // dropped rather than doubled up against ours.
    const reason = String(assignment?.overrideReason ?? '').trim().replace(/[.\s]+$/, '');
    return warning({
      code: WARNING_CODES.INELIGIBLE_ASSIGNED,
      severity: SEVERITY.INFO,
      dayKey,
      employeeId: entry.employeeId,
      slotMinutes: slot,
      message: `${name}'s ${dayLabel} lunch was chosen by a person, not by the rules`,
      detail: `A manager gave ${name} ${at} on ${dayLabel} (${chosen}). The rules on their own would `
        + `not have - ${lowerFirst(entry.reasonText)}${reason ? ` Reason given: "${reason}".` : ''} `
        + 'Nothing has been changed - this is here so the decision is visible, not to undo it.',
    });
  }

  return warning({
    code: WARNING_CODES.INELIGIBLE_ASSIGNED,
    severity: SEVERITY.WARNING,
    dayKey,
    employeeId: entry.employeeId,
    slotMinutes: slot,
    message: `${name} has ${at} on ${dayLabel} but is not eligible`,
    detail: `${name} holds ${at} on ${dayLabel} even though ${lowerFirst(entry.reasonText)} Either `
      + 'remove the lunch or add a staff rule that grants it, so the schedule and the rules agree.',
  });
}

/** An assignment whose employee is not on this week's rota at all. */
function unknownEmployeeAssignedWarning(assignment, dayKey, names) {
  const name = nameOf(names, assignment?.employeeId, assignment?.name);
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;
  const slot = slotOf(assignment);
  const at = slot === null ? 'a lunch' : `a lunch at ${formatMinutes(slot)}`;
  const theLunch = slot === null
    ? `The ${dayLabel} lunch`
    : `The ${formatMinutes(slot)} lunch on ${dayLabel}`;
  const chosen = humanChoice(assignment);

  return warning({
    code: WARNING_CODES.INELIGIBLE_ASSIGNED,
    severity: chosen ? SEVERITY.INFO : SEVERITY.WARNING,
    dayKey,
    employeeId: assignment?.employeeId ?? null,
    slotMinutes: slot,
    message: `${name} has ${at} on ${dayLabel} but is not on the rota`,
    detail: `${theLunch} belongs to "${name}"`
      + `${assignment?.employeeId ? ` (${assignment.employeeId})` : ''}, who has no row in this `
      + `week's rota${chosen ? ` - it was put there by hand (${chosen})` : ''}. Usually this means `
      + 'the name changed between imports, or the rota was replaced after the schedule was built.',
  });
}

/**
 * Where the break landed relative to the shift.
 *
 * The two guard-rail checks are skipped on the edge that already failed
 * `OUTSIDE_SHIFT`: a lunch starting before the shift does is one fault, and
 * reporting it twice would bury the second, genuinely different, faults.
 */
function placementWarnings({ assignment, shift, rule, dayConfig, lunchMinutes, dayKey, names }) {
  const slot = slotOf(assignment);
  if (slot === null) return [];
  if (!shift || shift.kind !== 'shift') return [];
  if (!Number.isFinite(shift.startMinutes) || !Number.isFinite(shift.endMinutes)) return [];

  const warnings = [];
  const name = nameOf(names, assignment.employeeId, assignment.name);
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;
  const duration = positive(assignment.durationMinutes, lunchMinutes);
  const end = slot + duration;
  const startsEarly = slot < shift.startMinutes;
  const endsLate = end > shift.endMinutes;

  if (startsEarly || endsLate) {
    const overrunText = startsEarly
      ? `${formatDuration(shift.startMinutes - slot)} before the shift starts`
      : `${formatDuration(end - shift.endMinutes)} past the end of the shift`;
    warnings.push(warning({
      code: WARNING_CODES.OUTSIDE_SHIFT,
      severity: SEVERITY.ERROR,
      dayKey,
      employeeId: assignment.employeeId,
      slotMinutes: slot,
      message: `${name}'s ${formatMinutes(slot)} lunch falls outside their ${dayLabel} shift`,
      detail: `The break runs ${formatMinutes(slot)}-${formatMinutes(end)} but the shift is `
        + `${shiftRange(shift)}, so it sits ${overrunText}. Move it back inside the shift.`,
    }));
  }

  // A per-employee guard rail replaces the day profile's rather than adding to
  // it, exactly as eligibility.js resolves it - the two must not disagree.
  const afterStart = Number.isFinite(rule?.minMinutesAfterShiftStart)
    ? rule.minMinutesAfterShiftStart
    : number(dayConfig?.minMinutesAfterShiftStart, 0);
  const beforeEnd = Number.isFinite(rule?.minMinutesBeforeShiftEnd)
    ? rule.minMinutesBeforeShiftEnd
    : number(dayConfig?.minMinutesBeforeShiftEnd, 0);

  const intoShift = slot - shift.startMinutes;
  if (!startsEarly && afterStart > 0 && intoShift < afterStart) {
    const howFarIn = intoShift === 0
      ? `${name}'s ${dayLabel} lunch starts the moment their shift does`
      : `${name} eats only ${formatDuration(intoShift)} into their ${dayLabel} shift`;
    warnings.push(warning({
      code: WARNING_CODES.TOO_SOON_AFTER_START,
      severity: SEVERITY.WARNING,
      dayKey,
      employeeId: assignment.employeeId,
      slotMinutes: slot,
      message: howFarIn,
      detail: `The shift starts at ${formatMinutes(shift.startMinutes)} and the break is at `
        + `${formatMinutes(slot)}, ${formatDuration(intoShift)} in. The rule for ${dayLabel} asks for `
        + `at least ${formatDuration(afterStart)}, so the earliest allowed start is `
        + `${formatMinutes(shift.startMinutes + afterStart)}.`,
    }));
  }

  const leftAfter = shift.endMinutes - end;
  if (!endsLate && beforeEnd > 0 && leftAfter < beforeEnd) {
    warnings.push(warning({
      code: WARNING_CODES.TOO_CLOSE_TO_END,
      severity: SEVERITY.WARNING,
      dayKey,
      employeeId: assignment.employeeId,
      slotMinutes: slot,
      message: `${name} has only ${formatDuration(leftAfter)} left after their ${dayLabel} lunch`,
      detail: `The break runs ${formatMinutes(slot)}-${formatMinutes(end)} and the shift finishes at `
        + `${formatMinutes(shift.endMinutes)}, leaving ${formatDuration(leftAfter)}. The rule for `
        + `${dayLabel} asks for at least ${formatDuration(beforeEnd)} of work after a break, so the `
        + `latest allowed start is ${formatMinutes(shift.endMinutes - beforeEnd - duration)}.`,
    }));
  }

  return warnings;
}

/** Two breaks in one day for one person. */
function doubleAssignedWarning(employeeId, placed, dayKey, names) {
  const name = nameOf(names, employeeId, placed[0]?.name);
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;
  const slots = placed
    .map((assignment) => slotOf(assignment))
    .filter((slot) => slot !== null)
    .sort((a, b) => a - b);
  const times = slots.length > 0 ? slots.map((slot) => formatMinutes(slot)) : ['an unplaced slot'];

  return warning({
    code: WARNING_CODES.DOUBLE_ASSIGNED,
    severity: SEVERITY.ERROR,
    dayKey,
    employeeId,
    // Anchored on the earliest of the clashing slots so the id is stable no
    // matter which order the assignments happen to be stored in.
    slotMinutes: slots.length > 0 ? slots[0] : null,
    message: `${name} has ${placed.length} lunches on ${dayLabel}`,
    detail: `${name} appears in ${dayLabel}'s lunches at ${listOf(times)} - one person, `
      + `${placed.length} breaks. All but one should be removed: the extras are also holding slots `
      + 'and coverage away from everybody else.',
  });
}

/* -------------------------------------------------------------------------- */
/* Whole-day checks                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Prefer the coverage grid the schedule was built with, so the warning and the
 * coverage strip on screen can never contradict each other, and only recompute
 * when there is none (a hand-edited or freshly restored schedule).
 */
function resolveCoverage({ day, shifts, assignments, dayConfig, settings }) {
  if (Array.isArray(day?.coverage) && day.coverage.length > 0) return day.coverage;
  return computeCoverage({ shifts, assignments, dayConfig, settings });
}

/**
 * Coverage breaches, collapsed to one warning per contiguous run.
 *
 * A 30-minute grid turns a single badly-placed lunch into two or three failing
 * buckets; listing each one would make a small problem look like a crisis and
 * would hide the shape of it. A run is one continuous period of understaffing - 
 * which is exactly the thing a manager fixes with one drag.
 *
 * Runs containing no lunch at all are skipped. `coverage.js` marks any thin
 * period as a breach, which is right for the coverage strip but wrong here: at
 * 06:30 one person is on the rota, nobody is at lunch, and no lunch decision can
 * change that. Reporting it would put a dozen unfixable errors on every week and
 * bury the ones a manager can actually act on.
 */
function coverageWarnings(buckets, dayKey, names) {
  const warnings = [];
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;

  for (const run of breachRuns(buckets, BREACH_BELOW_MINIMUM)) {
    const atLunch = runLunchNames(run, names);
    if (atLunch.length === 0) continue;

    const worst = run.buckets.reduce((low, bucket) => (bucket.remaining < low.remaining ? bucket : low));
    const required = number(worst.minRequired, 0);
    warnings.push(warning({
      code: WARNING_CODES.COVERAGE_BELOW_MINIMUM,
      severity: SEVERITY.ERROR,
      dayKey,
      slotMinutes: run.startMinutes,
      message: `Only ${worst.remaining} on the phones (minimum ${required}) `
        + `${formatMinutes(run.startMinutes)}-${formatMinutes(run.endMinutes)} on ${dayLabel}`,
      detail: `Between ${formatMinutes(run.startMinutes)} and ${formatMinutes(run.endMinutes)} cover `
        + `drops to ${worst.remaining} against a minimum of ${required}. At its worst `
        + `(${formatMinutes(worst.startMinutes)}) ${worst.scheduled} `
        + `${worst.scheduled === 1 ? 'person is' : 'people are'} rostered and ${worst.onLunch} `
        + `${worst.onLunch === 1 ? 'is' : 'are'} at lunch. Breaks inside that ${runLength(run)} `
        + `window: ${listOf(atLunch)} - move one of them to a quieter slot.`,
    }));
  }

  for (const run of breachRuns(buckets, BREACH_TOO_MANY_SIMULTANEOUS)) {
    const worst = run.buckets.reduce((high, bucket) => (bucket.onLunch > high.onLunch ? bucket : high));
    const allowed = number(worst.maxSimultaneous, 1);
    const nisccLimit = !isWeekend(dayKey)
      && worst.startMinutes < NISCC_SINGLE_LUNCH_CUTOFF_MINUTES;
    const eveningLimit = worst.startMinutes >= EVENING_SINGLE_LUNCH_FROM_MINUTES;
    let advice;
    if (eveningLimit) {
      advice = 'nobody shares a lunch slot from 17:00 onwards. Move one of them to the next slot.';
    } else if (nisccLimit) {
      advice = 'the NISCC weekday rule allows only one lunch at a time before 13:00. Stagger one of them.';
    } else {
      advice = `${dayLabel} allows ${allowed}. Either stagger one of them or raise the day's `
        + 'simultaneous-lunch limit in settings.';
    }
    warnings.push(warning({
      code: WARNING_CODES.TOO_MANY_SIMULTANEOUS,
      severity: SEVERITY.ERROR,
      dayKey,
      slotMinutes: run.startMinutes,
      message: `${worst.onLunch} lunches at once (limit ${allowed}) at `
        + `${formatMinutes(run.startMinutes)} on ${dayLabel}`,
      detail: `Between ${formatMinutes(run.startMinutes)} and ${formatMinutes(run.endMinutes)} up to `
        + `${worst.onLunch} people are at lunch at the same time${lunchNames(worst, names)}, but `
        + advice,
    }));
  }

  return warnings;
}

/** The solver gave up on this day. */
function noSolutionWarning(day, dayKey, names) {
  const dayLabel = DAY_LABELS[dayKey] ?? dayKey;
  const unassigned = Array.isArray(day.unassigned) ? day.unassigned : [];
  const who = unassigned.map((record) => nameOf(names, record.employeeId, record.name));
  const placed = Array.isArray(day.assignments) ? day.assignments.length : 0;

  const missing = who.length > 0
    ? `${who.length} ${who.length === 1 ? 'person was' : 'people were'} left out (${listOf(who)}).`
    : 'At least one person who was owed a lunch was left out.';
  const first = unassigned.find((record) => record.reasonText)?.reasonText ?? '';

  return warning({
    code: WARNING_CODES.NO_SOLUTION,
    severity: SEVERITY.ERROR,
    dayKey,
    message: `${dayLabel} could not be fully solved`,
    detail: `${placed} ${placed === 1 ? 'lunch' : 'lunches'} were placed on ${dayLabel}, but the day `
      + `is not solved: ${missing}${first ? ` For example: ${first}` : ''} Widening the lunch window, `
      + 'raising the simultaneous-lunch limit or lowering the minimum staff on duty for this day '
      + 'will usually clear it.',
  });
}

/* -------------------------------------------------------------------------- */
/* Week-scoped checks                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Checks that belong to the week rather than to a day.
 *
 * These carry `dayKey: null` and are returned once. Repeating "two rows are
 * called Josh" on all seven day cards would be seven times the noise for one
 * problem with one fix.
 */
function validateWeekScope({ rota, aliasBook, names }) {
  const warnings = [];

  /* Duplicate rota rows. -------------------------------------------------- */
  const rowsById = new Map();
  const rows = Array.isArray(rota?.rows) ? rota.rows : [];
  rows.forEach((row, index) => {
    const name = String(row?.name ?? '').trim();
    if (!name) return;
    const employeeId = row?.employeeId || slugify(name);
    if (!employeeId) return;
    if (!rowsById.has(employeeId)) rowsById.set(employeeId, []);
    rowsById.get(employeeId).push({ row, index, name });
  });

  for (const [employeeId, group] of rowsById) {
    if (group.length < 2) continue;
    const positions = group.map((item) => `row ${item.index + 1} ("${item.name}")`);
    warnings.push(warning({
      code: WARNING_CODES.DUPLICATE_EMPLOYEE,
      severity: SEVERITY.WARNING,
      employeeId,
      message: `${group.length} rota rows are the same person (${group[0].name})`,
      detail: `${listOf(positions)} ${group.length === 2 ? 'both resolve' : 'all resolve'} to the `
        + `employee id "${employeeId}", so their shifts, staff rules and lunches are being merged `
        + 'into one person. Rename whichever row belongs to somebody else, or delete the duplicate.',
    }));
  }

  /* Unresolved aliases. --------------------------------------------------- */
  for (const alias of aliasBook?.unresolved ?? []) {
    if (!alias || alias.resolvedTo) continue;
    const token = String(alias.token ?? '').trim();
    if (!token) continue;

    const suggestion = (alias.suggestions ?? [])[0] ?? null;
    const suggestedName = suggestion ? nameOf(names, suggestion.employeeId, '') : '';
    const suggestionText = suggestion
      ? ` Lunchly's best guess is ${suggestedName}${suggestion.confidence ? ` (${suggestion.confidence})` : ''}`
        + `${suggestion.reason ? `: ${suggestion.reason}` : '.'}`
      : ' Lunchly has no candidate for it.';

    warnings.push(warning({
      code: WARNING_CODES.UNRESOLVED_ALIAS,
      severity: SEVERITY.INFO,
      // The id needs one stable part per token; an alias has no employee, so the
      // slugged token stands in. Two unresolved names would otherwise share an
      // id and acknowledging one would silently dismiss the other.
      employeeId: slugify(token) || token,
      message: `"${token}" has not been matched to anybody`,
      detail: `The short name "${token}" appears in the historical lunch lists but is not mapped to `
        + `an employee, so nothing is scheduled for it.${suggestionText}`
        + `${alias.note ? ` ${alias.note}` : ''} Confirm or correct it in the alias panel - Lunchly `
        + 'will not apply a guess on its own.',
    }));
  }

  /* A PDF import nobody has checked. -------------------------------------- */
  const confidence = importConfidenceOf(rota);
  if (rota?.source === 'pdf' && confidence !== null && confidence < LOW_CONFIDENCE_THRESHOLD) {
    const percent = Math.round(confidence * 100);
    warnings.push(warning({
      code: WARNING_CODES.LOW_CONFIDENCE_IMPORT,
      severity: SEVERITY.WARNING,
      message: `This rota was read from a PDF with only ${percent}% confidence`,
      detail: `The PDF importer scored this rota ${confidence} out of 1, below the `
        + `${LOW_CONFIDENCE_THRESHOLD} it needs to be trusted unread. Columns may have been mixed up `
        + 'or times misread, and every lunch below is built on those times - check the rota grid '
        + 'against the original document before sending anything out.',
    }));
  }

  return warnings;
}

/**
 * Where an import left its confidence score.
 *
 * `normaliseRota` only keeps the canonical Rota fields, so importers hang their
 * score wherever suits them and this looks in each of the plausible places
 * rather than forcing one on them. A per-row score wins nothing by being
 * averaged, so the *worst* row is used: one badly-read row is enough reason to
 * look.
 */
function importConfidenceOf(rota) {
  const direct = [rota?.confidence, rota?.importConfidence, rota?.meta?.confidence, rota?.import?.confidence];
  for (const value of direct) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  let lowest = null;
  for (const row of rota?.rows ?? []) {
    const parsed = Number(row?.confidence);
    if (!Number.isFinite(parsed)) continue;
    if (lowest === null || parsed < lowest) lowest = parsed;
  }
  return lowest;
}

/* -------------------------------------------------------------------------- */
/* Warning construction, ordering and acknowledgement                         */
/* -------------------------------------------------------------------------- */

/**
 * Build one warning with its deterministic id.
 *
 * The id is `code:dayKey:employeeId:slotMinutes` with `-` standing in for any
 * part that does not apply, so the same complaint about the same person at the
 * same time always gets the same id - which is the whole mechanism by which an
 * acknowledgement outlives a regeneration.
 */
function warning({ code, severity, dayKey = null, employeeId = null, slotMinutes = null, message, detail }) {
  const day = dayKey ?? null;
  const employee = employeeId || null;
  const slot = Number.isFinite(slotMinutes) ? slotMinutes : null;
  return {
    id: [code, day ?? '-', employee ?? '-', slot === null ? '-' : String(slot)].join(':'),
    code,
    severity,
    dayKey: day,
    employeeId: employee,
    slotMinutes: slot,
    message,
    detail,
    acknowledged: false,
  };
}

/**
 * Every id a manager has already accepted.
 *
 * Two sources, because there are two ways to say "I meant that": dismissing the
 * warning itself, and an assignment carrying the code in `acknowledgedWarnings`
 * (how a manual override records that it knowingly broke a rule). Rebuilding the
 * id from the assignment is safe precisely because the id formula is fixed.
 */
function collectAcknowledgedIds(schedule) {
  const ids = new Set();
  const remember = (list) => {
    for (const item of list ?? []) {
      if (item?.acknowledged === true && item.id) ids.add(item.id);
    }
  };

  remember(schedule?.warnings);
  for (const dayKey of DAY_KEYS) {
    const day = schedule?.days?.[dayKey];
    remember(day?.warnings);
    for (const assignment of day?.assignments ?? []) {
      const slot = slotOf(assignment);
      for (const code of assignment?.acknowledgedWarnings ?? []) {
        if (!code) continue;
        ids.add([code, dayKey, assignment.employeeId || '-', slot === null ? '-' : String(slot)].join(':'));
      }
    }
  }

  return ids;
}

/**
 * Severity first, then the day, then the clock.
 *
 * Week-scoped warnings sort ahead of Monday and untimed warnings ahead of timed
 * ones, so a list always reads "here is what is wrong with the week, then what
 * is wrong with Monday, in the order Monday happens". The id is the final
 * tie-break purely so the order is total and reproducible.
 */
function compareWarnings(a, b) {
  const severity = severityRank(a.severity) - severityRank(b.severity);
  if (severity !== 0) return severity;

  const day = dayRank(a.dayKey) - dayRank(b.dayKey);
  if (day !== 0) return day;

  const time = timeRank(a.slotMinutes) - timeRank(b.slotMinutes);
  if (time !== 0) return time;

  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

function severityRank(severity) {
  return SEVERITY_RANK[severity] ?? SEVERITY_RANK.info;
}

function dayRank(dayKey) {
  const index = DAY_KEYS.indexOf(dayKey);
  return index < 0 ? -1 : index;
}

function timeRank(slotMinutes) {
  return Number.isFinite(slotMinutes) ? slotMinutes : -1;
}

/* -------------------------------------------------------------------------- */
/* Small shared helpers                                                       */
/* -------------------------------------------------------------------------- */

/** Use the shifts we were given; rebuild them only when there are none. */
function resolveWeekShifts(weekShifts, rota, settings) {
  if (weekShifts && DAY_KEYS.some((dayKey) => Array.isArray(weekShifts[dayKey]))) {
    return weekShifts;
  }
  if (!rota) return {};
  return buildWeekShifts(rota, settings);
}

/**
 * Full names for every employee we might have to talk about. Warnings name
 * people the way the rota does - the short Discord alias is for the export, not
 * for a message telling somebody their rota is wrong.
 */
function buildNameIndex(rota, shiftsByDay, schedule) {
  const names = new Map();
  const remember = (employeeId, name) => {
    if (!employeeId) return;
    const trimmed = String(name ?? '').trim();
    if (trimmed && !names.has(employeeId)) names.set(employeeId, trimmed);
  };

  for (const row of rota?.rows ?? []) remember(row?.employeeId || slugify(row?.name), row?.name);
  for (const dayKey of DAY_KEYS) {
    for (const shift of shiftsByDay?.[dayKey] ?? []) remember(shift?.employeeId, shift?.name);
    for (const assignment of schedule?.days?.[dayKey]?.assignments ?? []) {
      remember(assignment?.employeeId, assignment?.name);
    }
  }
  return names;
}

/** Best available human name, never an empty string. */
function nameOf(names, employeeId, fallback) {
  const direct = String(fallback ?? '').trim();
  if (direct) return direct;
  return names?.get?.(employeeId) || employeeId || 'this employee';
}

/** Finite slot start, or null when the assignment has not been placed. */
function slotOf(assignment) {
  const slot = assignment?.slotMinutes;
  return Number.isFinite(slot) ? slot : null;
}

/** How an assignment came to exist, when a person rather than the solver chose it. */
function humanChoice(assignment) {
  if (assignment?.manual === true) return 'placed by hand';
  if (assignment?.source === ASSIGNMENT_SOURCES.MANUAL) return 'placed by hand';
  if (assignment?.source === ASSIGNMENT_SOURCES.IMPORTED) return 'imported from an existing list';
  return null;
}

/** "08:30-14:30" - hyphenated, matching how managers write shifts on the rota. */
function shiftRange(shift) {
  return `${formatMinutes(shift?.startMinutes)}-${formatMinutes(shift?.endMinutes)}`;
}

/** Human duration of a coverage run, used in the "move a break out of…" advice. */
function runLength(run) {
  return formatDuration(run.endMinutes - run.startMinutes);
}

/** ", and" list of the people at lunch in a bucket, or nothing when unknown. */
function lunchNames(bucket, names) {
  const list = (bucket?.lunchEmployeeIds ?? [])
    .map((employeeId) => nameOf(names, employeeId, ''))
    .filter(Boolean);
  return list.length > 0 ? ` (${listOf(list)})` : '';
}

/**
 * Everybody whose break touches a run, in slot order and without repeats - a
 * lunch straddling two buckets is still one lunch to move.
 */
function runLunchNames(run, names) {
  const seen = new Set();
  const list = [];
  for (const bucket of run.buckets) {
    for (const employeeId of bucket.lunchEmployeeIds ?? []) {
      if (seen.has(employeeId)) continue;
      seen.add(employeeId);
      list.push(nameOf(names, employeeId, ''));
    }
  }
  return list.filter(Boolean);
}

/** "a", "a and b", "a, b and c". */
function listOf(items) {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Find the runs of consecutive buckets carrying one breach code.
 *
 * Buckets must be adjacent on the clock as well as in the array: a gap means the
 * problem cleared and came back, which is two things to fix, not one.
 */
function breachRuns(buckets, code) {
  const runs = [];
  if (!Array.isArray(buckets)) return runs;

  let current = null;
  for (const bucket of buckets) {
    if (!hasBreach(bucket, code)) {
      current = null;
      continue;
    }
    if (current && current.endMinutes === bucket.startMinutes) {
      current.buckets.push(bucket);
      current.endMinutes = bucket.endMinutes;
      continue;
    }
    current = {
      startMinutes: bucket.startMinutes,
      endMinutes: bucket.endMinutes,
      buckets: [bucket],
    };
    runs.push(current);
  }
  return runs;
}

/** Trust the bucket's own breach list; re-derive only if it has none. */
function hasBreach(bucket, code) {
  if (!bucket) return false;
  if (Array.isArray(bucket.breaches)) return bucket.breaches.includes(code);
  if (number(bucket.scheduled, 0) === 0 && number(bucket.onLunch, 0) === 0) return false;
  if (code === BREACH_BELOW_MINIMUM) return number(bucket.remaining, 0) < number(bucket.minRequired, 0);
  return number(bucket.onLunch, 0) > number(bucket.maxSimultaneous, 1);
}

/** "Not eligible: …" -> "not eligible: …" so it can be dropped mid-sentence. */
function lowerFirst(text) {
  const value = String(text ?? '').trim();
  if (!value) return 'the rules do not grant them one.';
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function number(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function positive(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
